(function () {
  const CARD_SELECTOR = '.\\#product-card';
  const CONTAINER_SELECTOR = '.\\#grid, .blaze-track';
  const GALLERY_COUNTER_SELECTOR = '[data-product-card-gallery-counter]';
  const ROW_SELECTOR = '[data-product-card-row]';
  const VARIANT_ROW_SELECTOR = '[data-product-card-variant-row]';
  const VARIANT_GROUP_SELECTOR = '[data-product-card-variant-group]';
  const VARIANT_ITEM_SELECTOR = '[data-product-card-variant-item]';
  const VARIANT_OVERFLOW_SELECTOR = '[data-product-card-variant-overflow]';
  const SWATCH_INPUT_SELECTOR = '.\\#product-card-swatch-input';
  const SWATCH_MEDIA_SELECTOR = '[data-swatch-media-number]';
  const MEDIA_BADGE_SELECTOR = '.\\#product-card-media-header .\\#product-card-badges .\\#badge';
  const BADGE_TEXT_SELECTOR = '.\\#product-card-badge-text';
  const ROW_TOLERANCE = 3;

  let scheduled = false;
  let scheduledVariantRows = false;
  const observedContainers = new Set();
  const observedVariantRows = new Set();
  const observedWidths = new WeakMap();
  const observedVariantWidths = new WeakMap();
  const pendingVariantRows = new Set();

  function getAlignmentContainers() {
    const containers = new Set();

    document.querySelectorAll(CARD_SELECTOR).forEach(card => {
      const container = card.closest(CONTAINER_SELECTOR);
      if (container) containers.add(container);
    });

    return Array.from(containers);
  }

  function getContainerCards(container) {
    return Array.from(container.querySelectorAll(CARD_SELECTOR)).filter(card => {
      return card.closest(CONTAINER_SELECTOR) === container && card.getClientRects().length > 0;
    });
  }

  function resetRows(cards) {
    cards.forEach(card => {
      card.querySelectorAll(ROW_SELECTOR).forEach(row => {
        row.style.removeProperty('--product-card-row-height');
        delete row.dataset.productCardRowState;
      });
    });
  }

  function getVariantRows(cards) {
    return cards.flatMap(card => Array.from(card.querySelectorAll(VARIANT_ROW_SELECTOR)));
  }

  function getVariantItems(group) {
    return Array.from(group.querySelectorAll(VARIANT_ITEM_SELECTOR)).filter(item => {
      return item.closest(VARIANT_GROUP_SELECTOR) === group;
    });
  }

  function getVariantOverflow(group) {
    return group.querySelector(VARIANT_OVERFLOW_SELECTOR);
  }

  function updateVariantOverflow(group) {
    const overflow = getVariantOverflow(group);
    if (!overflow) return;

    const hiddenCount = getVariantItems(group).filter(item => item.hidden).length;
    const count = overflow.querySelector('span');
    const context = overflow.dataset.productCardVariantOverflowContext;

    if (count && count.textContent !== `+${hiddenCount}`) {
      count.textContent = `+${hiddenCount}`;
    }

    overflow.hidden = hiddenCount === 0;
    overflow.dataset.productCardVariantOverflowCount = String(hiddenCount);

    if (context && hiddenCount > 0) {
      overflow.title = `+${hiddenCount} weitere ${context}`;
      overflow.setAttribute('aria-label', `+${hiddenCount} weitere ${context}`);
    } else {
      overflow.removeAttribute('title');
      overflow.removeAttribute('aria-label');
    }
  }

  function resetVariantRow(row) {
    row.querySelectorAll(VARIANT_ITEM_SELECTOR).forEach(item => {
      item.hidden = false;
    });
    row.querySelectorAll(VARIANT_GROUP_SELECTOR).forEach(updateVariantOverflow);
    row.removeAttribute('data-product-card-variant-overflow-compact');
  }

  function variantRowFits(row) {
    return row.scrollWidth <= row.clientWidth + 1;
  }

  function truncateVariantRow(row) {
    if (!row || row.getClientRects().length === 0) return;

    const items = Array.from(row.querySelectorAll(VARIANT_ITEM_SELECTOR));
    if (items.length === 0) return;

    resetVariantRow(row);
    if (variantRowFits(row)) return;

    const visibleItemsByGroup = new Map(
      Array.from(row.querySelectorAll(VARIANT_GROUP_SELECTOR)).map(group => {
        return [group, getVariantItems(group)];
      })
    );

    for (let index = items.length - 1; index >= 0; index -= 1) {
      const group = items[index].closest(VARIANT_GROUP_SELECTOR);
      const groupItems = visibleItemsByGroup.get(group) || [];
      const visibleItems = groupItems.filter(item => !item.hidden);

      // Keep one value from every rendered option group so a compact card
      // still exposes the meaning of each group (for example color and size).
      if (visibleItems.length <= 1) continue;

      items[index].hidden = true;
      updateVariantOverflow(group);

      if (variantRowFits(row)) return;
    }

    // A normal card is wider than the compact overflow controls. Keep one
    // value per group when an unusually narrow card cannot fit everything.
    row.setAttribute('data-product-card-variant-overflow-compact', '');
  }

  function truncateVariantRows(rows) {
    rows.forEach(truncateVariantRow);
  }

  function syncSwatchMedia(card) {
    const inputs = Array.from(card.querySelectorAll(`${SWATCH_INPUT_SELECTOR}:checked`));
    const selected = new Set(inputs.map(input => {
      return `${input.dataset.productCardVariantGroup || ''}:${input.value}`;
    }));

    card.querySelectorAll(SWATCH_MEDIA_SELECTOR).forEach(media => {
      const key = `${media.dataset.swatchMediaGroup || ''}:${media.dataset.swatchMediaNumber}`;
      media.style.opacity = selected.has(key) ? '1' : '0';
    });
  }

  function groupCardsByVisualRow(container, cards) {
    const containerTop = container.getBoundingClientRect().top;
    const items = cards
      .map(card => ({
        card,
        top: card.getBoundingClientRect().top - containerTop
      }))
      .sort((a, b) => a.top - b.top);
    const groups = [];

    items.forEach(item => {
      const currentGroup = groups[groups.length - 1];

      if (!currentGroup || Math.abs(item.top - currentGroup.top) > ROW_TOLERANCE) {
        groups.push({ top: item.top, cards: [item.card] });
      } else {
        currentGroup.cards.push(item.card);
      }
    });

    return groups.map(group => group.cards);
  }

  function getRowKeys(cards) {
    const keys = new Set();

    cards.forEach(card => {
      card.querySelectorAll(ROW_SELECTOR).forEach(row => {
        if (row.dataset.productCardRow) keys.add(row.dataset.productCardRow);
      });
    });

    return keys;
  }

  function getCardRows(card, key) {
    return Array.from(card.querySelectorAll(ROW_SELECTOR)).filter(row => {
      return row.dataset.productCardRow === key;
    });
  }

  function getRenderedRow(rows) {
    return rows.find(row => {
      const styles = window.getComputedStyle(row);
      return styles.display !== 'none' && row.getClientRects().length > 0;
    }) || rows[0];
  }

  function measureRow(row) {
    if (!row) return 0;
    return row.getBoundingClientRect().height;
  }

  function alignCardGroup(cards) {
    getRowKeys(cards).forEach(key => {
      const rowsByCard = cards.map(card => getCardRows(card, key));
      const renderedRows = rowsByCard.map(getRenderedRow);
      const heights = renderedRows.map(measureRow);
      const maxHeight = Math.ceil(Math.max(0, ...heights));
      const rowIsActive = maxHeight > 0;

      rowsByCard.forEach((rows, cardIndex) => {
        const cardHasContent = heights[cardIndex] > 0;

        rows.forEach(row => {
          row.dataset.productCardRowState = rowIsActive
            ? (cardHasContent ? 'content' : 'empty')
            : 'inactive';

          if (rowIsActive) {
            row.style.setProperty('--product-card-row-height', `${maxHeight}px`);
          }
        });
      });
    });
  }

  function syncTagBackgroundWidths(cards) {
    cards.forEach(card => {
      card.querySelectorAll(MEDIA_BADGE_SELECTOR).forEach(badge => {
        const text = badge.querySelector(BADGE_TEXT_SELECTOR);
        if (!text || !text.textContent.trim() || text.getClientRects().length === 0) return;

        const range = document.createRange();
        range.selectNodeContents(text);
        const lineRects = Array.from(range.getClientRects()).filter(rect => rect.width > 0);
        range.detach();
        if (lineRects.length === 0) return;

        if (lineRects.length > 1) {
          badge.setAttribute('data-product-card-multiline', '');
        } else {
          badge.removeAttribute('data-product-card-multiline');
        }

        const styles = window.getComputedStyle(badge);
        const horizontalPadding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
        const longestLine = Math.max(...lineRects.map(rect => rect.width));
        const badgeWidth = badge.getBoundingClientRect().width;
        const backgroundWidth = Math.min(badgeWidth, longestLine + horizontalPadding);
        const currentWidth = parseFloat(badge.style.getPropertyValue('--product-card-badge-bg-width'));

        if (!Number.isFinite(currentWidth) || Math.abs(currentWidth - backgroundWidth) > 0.25) {
          badge.style.setProperty('--product-card-badge-bg-width', `${backgroundWidth.toFixed(2)}px`);
        }
      });
    });
  }

  function syncVariantResizeObserver(rows) {
    if (!variantResizeObserver) return;

    const currentRows = new Set(rows);
    observedVariantRows.forEach(row => {
      if (!row.isConnected || !currentRows.has(row)) {
        variantResizeObserver.unobserve(row);
        observedVariantRows.delete(row);
        observedVariantWidths.delete(row);
      }
    });

    rows.forEach(row => {
      if (observedVariantRows.has(row)) return;
      observedVariantRows.add(row);
      observedVariantWidths.set(row, row.getBoundingClientRect().width);
      variantResizeObserver.observe(row);
    });
  }

  function scheduleVariantRow(row) {
    pendingVariantRows.add(row);
    if (scheduledVariantRows) return;
    scheduledVariantRows = true;

    window.requestAnimationFrame(() => {
      scheduledVariantRows = false;
      const rowsToUpdate = Array.from(pendingVariantRows);
      pendingVariantRows.clear();
      truncateVariantRows(rowsToUpdate);
    });
  }

  function syncResizeObserver(containers) {
    if (!window.ResizeObserver) return;

    observedContainers.forEach(container => {
      if (!container.isConnected || !containers.includes(container)) {
        resizeObserver.unobserve(container);
        observedContainers.delete(container);
      }
    });

    containers.forEach(container => {
      if (observedContainers.has(container)) return;
      observedContainers.add(container);
      observedWidths.set(container, container.getBoundingClientRect().width);
      resizeObserver.observe(container);
    });
  }

  function alignProductCards() {
    const containers = getAlignmentContainers();
    const visibleCards = Array.from(document.querySelectorAll(CARD_SELECTOR)).filter(card => {
      return card.getClientRects().length > 0;
    });
    const cards = containers.flatMap(getContainerCards);
    const variantRows = getVariantRows(visibleCards);

    truncateVariantRows(variantRows);
    visibleCards.forEach(syncSwatchMedia);
    resetRows(cards);
    syncTagBackgroundWidths(cards);

    containers.forEach(container => {
      if (container.getClientRects().length === 0 || container.getBoundingClientRect().width === 0) return;

      const containerCards = getContainerCards(container);
      groupCardsByVisualRow(container, containerCards).forEach(alignCardGroup);
    });

    syncResizeObserver(containers);
    syncVariantResizeObserver(variantRows);
  }

  function scheduleAlignment() {
    if (scheduled) return;
    scheduled = true;

    window.requestAnimationFrame(() => {
      scheduled = false;
      alignProductCards();
    });
  }

  function elementContainsOrBelongsToCard(element) {
    if (!(element instanceof Element)) return false;

    return element.matches(CARD_SELECTOR)
      || element.matches(CONTAINER_SELECTOR)
      || Boolean(element.closest(CARD_SELECTOR))
      || Boolean(element.querySelector(CARD_SELECTOR));
  }

  function mutationAffectsCards(mutation) {
    if (mutation.type === 'characterData') {
      if (mutation.target.parentElement?.closest(GALLERY_COUNTER_SELECTOR)) return false;
      if (mutation.target.parentElement?.closest(VARIANT_OVERFLOW_SELECTOR)) return false;
      return Boolean(mutation.target.parentElement?.closest(CARD_SELECTOR));
    }

    if (mutation.target instanceof Element && mutation.target.closest(GALLERY_COUNTER_SELECTOR)) {
      return false;
    }

    if (mutation.type === 'attributes') {
      if (
        mutation.attributeName === 'style'
        && mutation.target.matches(MEDIA_BADGE_SELECTOR)
      ) {
        return false;
      }

      if (
        mutation.attributeName === 'style'
        && (
          mutation.target.matches(ROW_SELECTOR)
          || mutation.target.matches('.blaze-track')
          || mutation.target.matches(VARIANT_ITEM_SELECTOR)
          || mutation.target.matches(VARIANT_OVERFLOW_SELECTOR)
          || mutation.target.matches(SWATCH_MEDIA_SELECTOR)
        )
      ) {
        return false;
      }

      if (
        mutation.attributeName === 'hidden'
        && (
          mutation.target.matches(VARIANT_ITEM_SELECTOR)
          || mutation.target.matches(VARIANT_OVERFLOW_SELECTOR)
        )
      ) {
        return false;
      }

      return elementContainsOrBelongsToCard(mutation.target);
    }

    if (mutation.target instanceof Element) {
      if (mutation.target.matches(CONTAINER_SELECTOR) || mutation.target.closest(CARD_SELECTOR)) {
        return true;
      }
    }

    return [...mutation.addedNodes, ...mutation.removedNodes].some(node => {
      return node instanceof Element && (
        node.matches(CARD_SELECTOR)
        || node.matches(CONTAINER_SELECTOR)
        || Boolean(node.querySelector(CARD_SELECTOR))
      );
    });
  }

  const resizeObserver = window.ResizeObserver
    ? new ResizeObserver(entries => {
        const widthChanged = entries.some(entry => {
          const previousWidth = observedWidths.get(entry.target);
          const currentWidth = entry.contentRect.width;
          observedWidths.set(entry.target, currentWidth);
          return previousWidth === undefined || Math.abs(currentWidth - previousWidth) > 0.5;
        });

        if (widthChanged) scheduleAlignment();
      })
    : null;

  const variantResizeObserver = window.ResizeObserver
    ? new ResizeObserver(entries => {
        entries.forEach(entry => {
          const previousWidth = observedVariantWidths.get(entry.target);
          const currentWidth = entry.contentRect.width;
          observedVariantWidths.set(entry.target, currentWidth);

          if (previousWidth === undefined || Math.abs(currentWidth - previousWidth) > 0.5) {
            scheduleVariantRow(entry.target);
          }
        });
      })
    : null;

  const mutationObserver = new MutationObserver(mutations => {
    if (mutations.some(mutationAffectsCards)) scheduleAlignment();
  });

  document.addEventListener('change', event => {
    if (!(event.target instanceof Element) || !event.target.matches(SWATCH_INPUT_SELECTOR)) return;

    const card = event.target.closest(CARD_SELECTOR);
    if (card) syncSwatchMedia(card);
  });

  mutationObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'hidden', 'style'],
    characterData: true,
    childList: true,
    subtree: true
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleAlignment, { once: true });
  } else {
    scheduleAlignment();
  }

  window.addEventListener('load', scheduleAlignment, { once: true });
  window.addEventListener('pageshow', scheduleAlignment);
  window.addEventListener('resize', scheduleAlignment);
  document.addEventListener('shopify:section:load', scheduleAlignment);

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(scheduleAlignment).catch(() => {});
  }
})();
